import { useCallback, useSyncExternalStore } from 'react'
import type { LocalVideoTrack } from 'livekit-client'

/** Состояние привязано к LiveKit-треку и переживает смену его исходного MediaStreamTrack. */
type LoadingState = {
  loading: boolean
  listeners: Set<() => void>
}

const loadingStates = new WeakMap<LocalVideoTrack, LoadingState>()

/** Возвращает общее состояние камеры без удержания остановленных треков в памяти.
 * @param track — локальная камера.
 */
function getLoadingState(track: LocalVideoTrack): LoadingState {
  let state = loadingStates.get(track)

  if (!state) {
    state = { loading: false, listeners: new Set() }
    loadingStates.set(track, state)
  }

  return state
}

/** Уведомляет превью и комнату о скачивании весов и запуске модели.
 * @param track — локальная камера.
 * @param loading — загрузка продолжается до первого обработанного кадра.
 */
export function setBackgroundModelLoading(track: LocalVideoTrack, loading: boolean): void {
  const state = getLoadingState(track)

  if (state.loading === loading) return

  state.loading = loading

  // React может переподписать представление синхронно; новые подписки уведомим уже следующим изменением.
  for (const listener of [...state.listeners]) {
    listener()
  }
}

/** На сервере модель ещё не запускается, поэтому индикатор всегда выключен. */
function getServerSnapshot(): boolean {
  return false
}

/** Подписывает интерфейс на загрузку модели конкретной камеры, включая перезапуск устройства.
 * @param track — локальная камера; для удалённых треков передаётся undefined.
 */
export function useBackgroundModelLoading(track: LocalVideoTrack | undefined): boolean {
  const subscribe = useCallback((listener: () => void) => {
    if (!track) return () => {}

    const state = getLoadingState(track)

    state.listeners.add(listener)

    return () => { state.listeners.delete(listener) }
  }, [track])
  const getSnapshot = useCallback(() => track ? loadingStates.get(track)?.loading ?? false : false, [track])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
